// Centralised country / currency configuration for TaskRatel.
// Local currency is the source of truth for every transaction.
// A user's selected display currency is only ever an approximate reference.

export interface CurrencyConfig {
  code: string;
  symbol: string;
  name: string;
  decimals: number;
  locale: string;
}

export interface CountryConfig {
  country_id: string; // matches src/data/countries.ts id where applicable
  country_code: string;
  country_name: string;
  currency_code: string;
  locale: string;
  supported_payment_gateways: string[];
  supported_payment_methods: string[];
  supported_payout_methods: string[];
  mobile_topup_availability: boolean;
  gift_card_availability: boolean;
  wallet_availability: boolean;
  provider_payment_availability: boolean;
}

export const CURRENCIES: Record<string, CurrencyConfig> = {
  NGN: { code: "NGN", symbol: "₦", name: "Nigerian Naira", decimals: 0, locale: "en-NG" },
  GHS: { code: "GHS", symbol: "GH₵", name: "Ghanaian Cedi", decimals: 2, locale: "en-GH" },
  KES: { code: "KES", symbol: "KSh", name: "Kenyan Shilling", decimals: 0, locale: "en-KE" },
  ZAR: { code: "ZAR", symbol: "R", name: "South African Rand", decimals: 2, locale: "en-ZA" },
  XOF: { code: "XOF", symbol: "CFA", name: "West African CFA Franc", decimals: 0, locale: "fr-SN" },
  XAF: { code: "XAF", symbol: "FCFA", name: "Central African CFA Franc", decimals: 0, locale: "fr-CM" },
  RWF: { code: "RWF", symbol: "RF", name: "Rwandan Franc", decimals: 0, locale: "en-RW" },
  UGX: { code: "UGX", symbol: "USh", name: "Ugandan Shilling", decimals: 0, locale: "en-UG" },
  TZS: { code: "TZS", symbol: "TSh", name: "Tanzanian Shilling", decimals: 0, locale: "en-TZ" },
  ZMW: { code: "ZMW", symbol: "ZK", name: "Zambian Kwacha", decimals: 2, locale: "en-ZM" },
  MWK: { code: "MWK", symbol: "MK", name: "Malawian Kwacha", decimals: 0, locale: "en-MW" },
  EGP: { code: "EGP", symbol: "E£", name: "Egyptian Pound", decimals: 2, locale: "en-EG" },
  USD: { code: "USD", symbol: "$", name: "US Dollar", decimals: 2, locale: "en-US" },
  CAD: { code: "CAD", symbol: "C$", name: "Canadian Dollar", decimals: 2, locale: "en-CA" },
  GBP: { code: "GBP", symbol: "£", name: "British Pound", decimals: 2, locale: "en-GB" },
  EUR: { code: "EUR", symbol: "€", name: "Euro", decimals: 2, locale: "en-IE" },
};

const africanDefaults = {
  supported_payment_gateways: ["flutterwave"],
  supported_payment_methods: ["card", "bank_transfer", "mobile_money"],
  supported_payout_methods: ["bank_transfer"],
  mobile_topup_availability: true,
  gift_card_availability: true,
  wallet_availability: false,
  provider_payment_availability: false,
};

export const COUNTRIES: CountryConfig[] = [
  {
    country_id: "nigeria", country_code: "NG", country_name: "Nigeria", currency_code: "NGN", locale: "en-NG",
    supported_payment_gateways: ["paystack", "flutterwave", "opay"],
    supported_payment_methods: ["card", "bank_transfer", "ussd"],
    supported_payout_methods: ["bank_transfer"],
    mobile_topup_availability: true, gift_card_availability: true,
    wallet_availability: true, provider_payment_availability: true,
  },
  { country_id: "ghana", country_code: "GH", country_name: "Ghana", currency_code: "GHS", locale: "en-GH", ...africanDefaults },
  { country_id: "kenya", country_code: "KE", country_name: "Kenya", currency_code: "KES", locale: "en-KE", ...africanDefaults },
  { country_id: "south-africa", country_code: "ZA", country_name: "South Africa", currency_code: "ZAR", locale: "en-ZA", ...africanDefaults },
  { country_id: "cote-divoire", country_code: "CI", country_name: "Côte d'Ivoire", currency_code: "XOF", locale: "fr-CI", ...africanDefaults },
  { country_id: "rwanda", country_code: "RW", country_name: "Rwanda", currency_code: "RWF", locale: "en-RW", ...africanDefaults },
  { country_id: "uganda", country_code: "UG", country_name: "Uganda", currency_code: "UGX", locale: "en-UG", ...africanDefaults },
  { country_id: "tanzania", country_code: "TZ", country_name: "Tanzania", currency_code: "TZS", locale: "en-TZ", ...africanDefaults },
  { country_id: "zambia", country_code: "ZM", country_name: "Zambia", currency_code: "ZMW", locale: "en-ZM", ...africanDefaults },
  { country_id: "malawi", country_code: "MW", country_name: "Malawi", currency_code: "MWK", locale: "en-MW", ...africanDefaults },
  { country_id: "cameroon", country_code: "CM", country_name: "Cameroon", currency_code: "XAF", locale: "fr-CM", ...africanDefaults },
  { country_id: "senegal", country_code: "SN", country_name: "Senegal", currency_code: "XOF", locale: "fr-SN", ...africanDefaults },
  { country_id: "benin", country_code: "BJ", country_name: "Benin Republic", currency_code: "XOF", locale: "fr-BJ", ...africanDefaults },
  { country_id: "egypt", country_code: "EG", country_name: "Egypt", currency_code: "EGP", locale: "en-EG", ...africanDefaults },
  {
    country_id: "canada", country_code: "CA", country_name: "Canada", currency_code: "CAD", locale: "en-CA",
    supported_payment_gateways: ["stripe"], supported_payment_methods: ["card"], supported_payout_methods: [],
    mobile_topup_availability: false, gift_card_availability: true, wallet_availability: false, provider_payment_availability: false,
  },
  {
    country_id: "united-states", country_code: "US", country_name: "United States", currency_code: "USD", locale: "en-US",
    supported_payment_gateways: ["stripe"], supported_payment_methods: ["card"], supported_payout_methods: [],
    mobile_topup_availability: false, gift_card_availability: true, wallet_availability: false, provider_payment_availability: false,
  },
  {
    country_id: "united-kingdom", country_code: "GB", country_name: "United Kingdom", currency_code: "GBP", locale: "en-GB",
    supported_payment_gateways: ["stripe"], supported_payment_methods: ["card"], supported_payout_methods: [],
    mobile_topup_availability: false, gift_card_availability: true, wallet_availability: false, provider_payment_availability: false,
  },
];

/** Display currencies users may pick as a reference currency. */
export const DISPLAY_CURRENCY_CODES = [
  "NGN", "GHS", "KES", "ZAR", "XOF", "XAF", "RWF", "UGX", "TZS", "ZMW", "EGP", "USD", "CAD", "GBP", "EUR",
];

export const FALLBACK_CURRENCY = "USD";

export function getCountryConfig(countryId?: string | null): CountryConfig | undefined {
  if (!countryId) return undefined;
  const key = countryId.toLowerCase();
  return COUNTRIES.find((c) => c.country_id === key || c.country_code.toLowerCase() === key);
}

/** Currency of the service location — the source-of-truth transaction currency. */
export function currencyForCountry(countryId?: string | null): string {
  return getCountryConfig(countryId)?.currency_code ?? "NGN";
}

export function getCurrency(code?: string | null): CurrencyConfig {
  return CURRENCIES[(code || "").toUpperCase()] ?? CURRENCIES[FALLBACK_CURRENCY];
}

export function currencySymbol(code?: string | null): string {
  return getCurrency(code).symbol;
}

/** Single reusable money formatter. Never concatenate symbols by hand. */
export function formatMoney(
  amount: number | null | undefined,
  code: string = "NGN",
  opts: { withCode?: boolean; approx?: boolean } = {},
): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return "—";
  const cfg = getCurrency(code);
  let body: string;
  try {
    body = new Intl.NumberFormat(cfg.locale, {
      style: "currency",
      currency: cfg.code,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: cfg.decimals,
      maximumFractionDigits: cfg.decimals,
    }).format(amount);
  } catch {
    body = `${cfg.symbol}${amount.toLocaleString(undefined, {
      minimumFractionDigits: cfg.decimals,
      maximumFractionDigits: cfg.decimals,
    })}`;
  }
  if (opts.withCode) body = `${body} ${cfg.code}`;
  return opts.approx ? `≈ ${body}` : body;
}

/** Round to the currency's minor unit using safe decimal arithmetic. */
export function roundMoney(amount: number, code: string): number {
  const factor = Math.pow(10, getCurrency(code).decimals);
  return Math.round((amount + Number.EPSILON) * factor) / factor;
}

/** Detect a country from the browser locale, used only as a weak hint. */
export function countryFromBrowserLocale(): CountryConfig | undefined {
  if (typeof navigator === "undefined") return undefined;
  try {
    const region = new Intl.Locale(navigator.language || "en-US").maximize().region;
    return COUNTRIES.find((c) => c.country_code === region);
  } catch {
    return COUNTRIES.find((c) => c.country_code === "US");
  }
}

export type PriceType = "free" | "contact" | "hourly" | "daily" | "fixed" | "negotiable";

export const PRICE_TYPE_LABELS: Record<PriceType, string> = {
  free: "Free",
  contact: "Chat for price",
  hourly: "Per hour",
  daily: "Per day",
  fixed: "Fixed price (per job)",
  negotiable: "Negotiable",
};

/** Render a listing price honouring free / chat-for-price / rate types. */
export function formatListingPrice(
  price: number | null | undefined,
  priceType: string | null | undefined,
  currencyCode: string | null | undefined,
): string {
  const type = (priceType || "fixed") as PriceType;
  if (type === "free") return "Free";
  if (type === "contact" || price === null || price === undefined) return "Chat for price";
  const base = formatMoney(price, currencyCode || "NGN");
  if (type === "hourly") return `${base}/hr`;
  if (type === "daily") return `${base}/day`;
  if (type === "negotiable") return `${base} (negotiable)`;
  return base;
}